import { useState, useEffect, createContext, useContext } from "react";
import echo from "@/lib/echo";

/**
 * - `online`    — a configured client with a live socket.
 * - `degraded`  — a configured client whose socket is disconnected.
 * - `offline`   — a configured client whose socket failed or is unavailable.
 * - `disabled`  — no client at all: this build carries no Pusher configuration.
 *
 * `disabled` is deliberately not `offline`. Both mean "nothing will be pushed
 * to this tab", but only `offline` means something broke; `disabled` says the
 * image was built without `VITE_PUSHER_KEY` / `VITE_PUSHER_CLUSTER` while the
 * API is answering normally. Collapsing the two made the banner claim a server
 * outage on every production build that had not received the Pusher build args
 * yet (2026-09-11).
 */
export type ConnectionStatus = "online" | "degraded" | "offline" | "disabled";

const ConnectionHealthContext = createContext<ConnectionStatus>("online");

export function useConnectionHealth(): ConnectionStatus {
  return useContext(ConnectionHealthContext);
}

export { ConnectionHealthContext };

/**
 * Hook that monitors Pusher connection state.
 * Returns current connection status for use in the provider.
 */
export function useConnectionHealthState(): ConnectionStatus {
  const [status, setStatus] = useState<ConnectionStatus>("online");

  useEffect(() => {
    // A build with no Pusher configuration has no client at all (lib/echo.ts).
    // Nothing will ever be pushed to this tab, and nothing is broken either.
    if (!echo) {
      setStatus("disabled");
      return;
    }

    const pusher = (echo.connector as any).pusher;
    if (!pusher?.connection) return;

    const handler = ({ current }: { current: string }) => {
      switch (current) {
        case "connected":
          setStatus("online");
          break;
        case "connecting":
          // Don't change status during brief reconnections
          break;
        case "unavailable":
        case "failed":
          setStatus("offline");
          break;
        default:
          // disconnected, etc.
          setStatus("degraded");
          break;
      }
    };

    pusher.connection.bind("state_change", handler);
    return () => {
      pusher.connection.unbind("state_change", handler);
    };
  }, []);

  return status;
}
